import { useEffect, useState } from "react";
import { Link } from "wouter";
import CustomLink from "./CustomLink";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Landmark, Utensils, Mountain, Umbrella, History, Heater } from "lucide-react";
import { collection, getDocs, query, where } from "firebase/firestore";
import db from "../lib/firebaseDb";

interface Destination {
  id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
  isFeatured: boolean;
  activities?: string[];
}

// Hardcoded featured destinations with verified working images
const hardcodedDestinations: Destination[] = [
  {
    id: "istanbul",
    name: "Istanbul",
    slug: "istanbul",
    description: "Turkey's largest city bridging Europe and Asia with rich history and culture.",
    imageUrl: "/images/destinations/istanbul.jpg",
    isFeatured: true,
    activities: ["Historical Sightseeing", "Bosphorus Cruise"]
  },
  {
    id: "cappadocia",
    name: "Cappadocia",
    slug: "cappadocia",
    description: "Known for its fairy chimneys, cave dwellings, and hot air balloon rides.",
    imageUrl: "/images/destinations/cappadocia.jpg",
    isFeatured: true,
    activities: ["Hot Air Ballooning", "Cave Exploration"]
  },
  {
    id: "ankara",
    name: "Ankara",
    slug: "ankara",
    description: "Turkey's capital city, home to Anıtkabir and many important government institutions.",
    imageUrl: "/images/destinations/ankara.jpg",
    isFeatured: true,
    activities: ["Historical Sites", "Museums"]
  },
  {
    id: "antalya",
    name: "Antalya",
    slug: "antalya",
    description: "Mediterranean gem with beautiful beaches, ancient ruins, and luxury resorts.",
    imageUrl: "/images/destinations/antalya.jpg",
    isFeatured: true,
    activities: ["Beach Activities", "Ancient Ruins"]
  },
  {
    id: "pamukkale",
    name: "Pamukkale",
    slug: "pamukkale",
    description: "Natural wonder of white travertine terraces and thermal pools.",
    imageUrl: "/images/destinations/pamukkale.jpg",
    isFeatured: true,
    activities: ["Thermal Bathing", "Ancient Ruins"]
  },
  {
    id: "bodrum",
    name: "Bodrum",
    slug: "bodrum",
    description: "Trendy coastal town with beautiful beaches, vibrant nightlife, and historical attractions.",
    imageUrl: "/images/destinations/bodrum.jpg",
    isFeatured: true,
    activities: ["Beach Life", "Nightlife"]
  }
];

export default function FeaturedDestinations() {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedDestinations = async () => {
      try {
        setIsLoading(true);
        
        // First try to fetch from Firebase
        try {
          const q = query(collection(db, "destinations"), where("isFeatured", "==", true));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const featuredDestinations = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })) as Destination[];
            
            // Validate image URLs - replace with hardcoded if needed
            const validatedDestinations = featuredDestinations.map(dest => {
              const hardcoded = hardcodedDestinations.find(h => h.name === dest.name);
              if (hardcoded) {
                return {
                  ...dest,
                  imageUrl: hardcoded.imageUrl
                };
              }
              return dest;
            });
            
            // Ensure Istanbul, Cappadocia, and Antalya are the first three destinations
            const orderedDestinations = [];
            const priorityDestinations = ["Istanbul", "Cappadocia", "Antalya"];
            
            // First add the priority destinations in order
            for (const name of priorityDestinations) {
              const dest = validatedDestinations.find(d => d.name === name);
              if (dest) {
                orderedDestinations.push(dest);
              }
            }
            
            // Then add remaining destinations
            for (const dest of validatedDestinations) {
              if (!priorityDestinations.includes(dest.name)) {
                orderedDestinations.push(dest);
              }
            }
            
            setDestinations(orderedDestinations);
          } else {
            // Use hardcoded data if Firebase returns empty
            const orderedDestinations = [];
            const priorityDestinations = ["Istanbul", "Cappadocia", "Antalya"];
            
            // First add the priority destinations in order
            for (const name of priorityDestinations) {
              const dest = hardcodedDestinations.find(d => d.name === name);
              if (dest) {
                orderedDestinations.push(dest);
              }
            }
            
            // Then add remaining destinations
            for (const dest of hardcodedDestinations) {
              if (!priorityDestinations.includes(dest.name)) {
                orderedDestinations.push(dest);
              }
            }
            
            setDestinations(orderedDestinations);
          }
        } catch (fbError) {
          console.error("Error fetching from Firebase:", fbError);
          // Fallback to hardcoded data in ordered form
          const orderedDestinations = [];
          const priorityDestinations = ["Istanbul", "Cappadocia", "Antalya"];
          
          // First add the priority destinations in order
          for (const name of priorityDestinations) {
            const dest = hardcodedDestinations.find(d => d.name === name);
            if (dest) {
              orderedDestinations.push(dest);
            }
          }
          
          // Then add remaining destinations
          for (const dest of hardcodedDestinations) {
            if (!priorityDestinations.includes(dest.name)) {
              orderedDestinations.push(dest);
            }
          }
          
          setDestinations(orderedDestinations);
        }
      } catch (err) {
        console.error("Error fetching featured destinations:", err);
        setError("Failed to load destinations");
        
        // Still show hardcoded data on error, but in the correct order
        const orderedDestinations = [];
        const priorityDestinations = ["Istanbul", "Cappadocia", "Antalya"];
        
        // First add the priority destinations in order
        for (const name of priorityDestinations) {
          const dest = hardcodedDestinations.find(d => d.name === name);
          if (dest) {
            orderedDestinations.push(dest);
          }
        }
        
        // Then add remaining destinations
        for (const dest of hardcodedDestinations) {
          if (!priorityDestinations.includes(dest.name)) {
            orderedDestinations.push(dest);
          }
        }
        
        setDestinations(orderedDestinations);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedDestinations();
  }, []);

  const getTagIcon = (tag: string) => {
    switch (tag) {
      case "Historical Sightseeing":
      case "Historical Tours":
      case "Historical Sites":
        return <Landmark className="mr-1" size={12} />;
      case "Shopping":
        return <Utensils className="mr-1" size={12} />;
      case "Hiking":
      case "Cave Exploration":
        return <Mountain className="mr-1" size={12} />;
      case "Hot Air Ballooning":
        return <Heater className="mr-1" size={12} />;
      case "Beach Activities":
      case "Beach Life":
        return <Umbrella className="mr-1" size={12} />;
      case "Ancient Ruins":
      case "Archaeological Sites":
        return <History className="mr-1" size={12} />;
      default:
        return null;
    }
  };

  if (error && destinations.length === 0) {
    return (
      <section className="py-12 bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-sans font-bold text-[#333333] mb-8 text-center">
            Featured Destinations
          </h2>
          <div className="flex justify-center">
            <p className="text-red-500">Failed to load destinations. Please try again later.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-[#F5F5F5]">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-sans font-bold text-[#333333] mb-8 text-center">
          Featured Destinations
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {isLoading ? (
            // Skeleton loading state
            Array(3).fill(0).map((_, index) => (
              <Card key={index} className="overflow-hidden">
                <Skeleton className="h-56 w-full" />
                <CardContent className="p-5">
                  <Skeleton className="h-6 w-3/4 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4" />
                </CardContent>
              </Card>
            ))
          ) : (
            // Actual destinations - limit to 3 for featured section
            destinations.slice(0, 3).map((destination) => (
              <Card key={destination.id} className="bg-white rounded-lg overflow-hidden shadow-md group hover:shadow-lg transition duration-200">
                <div className="relative h-48 md:h-56 overflow-hidden">
                  <img
                    src={destination.imageUrl}
                    alt={destination.name}
                    loading="lazy"
                    width={800}
                    height={450}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    onError={(e) => {
                      // Fallback to a hardcoded image if the image fails to load
                      const hardcoded = hardcodedDestinations.find(h => h.name === destination.name);
                      if (hardcoded) {
                        (e.target as HTMLImageElement).src = hardcoded.imageUrl;
                      } else {
                        (e.target as HTMLImageElement).src = "/images/destinations/istanbul.jpg";
                      }
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                    <h3 className="text-xl font-sans font-bold text-white">{destination.name}</h3>
                  </div>
                </div>
                <CardContent className="p-5">
                  <p className="text-gray-600 mb-4">{destination.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {destination.activities && destination.activities.slice(0, 2).map((activity, index) => (
                        <span 
                          key={index} 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            index % 2 === 0 
                              ? "bg-[#30D5C8] bg-opacity-10 text-[#30D5C8]" 
                              : "bg-[#E30A17] bg-opacity-10 text-[#E30A17]"
                          }`}
                        >
                          {getTagIcon(activity)} {activity}
                        </span>
                      ))}
                    </div>
                    <CustomLink href={`/destinations/${destination.slug}`}>
                      <span className="text-[#30D5C8] font-sans font-semibold hover:text-[#E30A17] transition duration-200 cursor-pointer">
                        Explore <span className="ml-1">→</span>
                      </span>
                    </CustomLink>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
        
        <div className="text-center mt-8">
          <CustomLink href="/destinations">
            <div className="inline-block bg-[#30D5C8] text-white font-sans font-semibold py-3 px-6 rounded-lg hover:bg-opacity-90 transition duration-200 cursor-pointer">
              View All Destinations <span className="ml-2">→</span>
            </div>
          </CustomLink>
        </div>
      </div>
    </section>
  );
}
